import { Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import ConfirmDialog from "@buildpy/ui/confirm-dialog";
import EmptyState from "@buildpy/ui/empty-state";
import { Button } from "@buildpy/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@buildpy/ui/dialog";
import { useSignedUrls } from "@/lib/signed-url";
import { cn } from "@/lib/utils";
import { deleteProjectPhoto } from "@/api/actions/project-log";
import { projectPhotoPath } from "@buildpy/core/modules/project/photo-view";
import PhotoFallback, { usePhotoFailure } from "@/modules/project/ui/photo-fallback";
import { useRecordAction } from "@/modules/project/ui/use-record-action";

export interface PhotoGridItem {
  id: string;
  caption: string;
  // 공정 태그 · 올린 사람 · 시각 — 크게 볼 때 한 줄로.
  meta: string;
  canDelete: boolean;
}

interface Props {
  projectId: string;
  photos: readonly PhotoGridItem[];
  testId?: string;
  gridClassName?: string;
}

interface ThumbProps {
  // 서명 주소를 받기 전에는 비어 있다(SPA 는 Bearer 라 img 가 서명 없이는 못 연다).
  src: string | undefined;
  alt: string;
  className: string;
  withLink?: boolean;
}

// 안 뜨는 사진은 깨진 그림 대신 이유(세션 만료·없음·잠시 후)를 보인다.
const Thumb = ({ src, alt, className, withLink }: ThumbProps) => {
  const { failure, onError } = usePhotoFailure(src ?? "", "member");
  if (failure) return <PhotoFallback className={className} failure={failure} withLink={withLink} />;
  return <img alt={alt} className={className} loading="lazy" src={src} onError={onError} />;
};

// 오늘 사진 — 3열 정사각 격자, 누르면 크게 보고 지운다.
const PhotoGrid = ({ projectId, photos, testId = "today-photo-grid", gridClassName = "grid-cols-3" }: Props) => {
  const { pending, run } = useRecordAction();
  const srcOf = useSignedUrls(photos.map((p) => projectPhotoPath(projectId, p.id)));
  const [openId, setOpenId] = useState<string | null>(null);
  const current = photos.find((p) => p.id === openId) ?? null;
  // 크게 보기 창 위에 한 겹 더 띄우는 지우기 확인.
  const [asking, setAsking] = useState(false);
  // 사진을 열거나 닫거나 바꿀 때 지우기 확인을 내린다 — 다음 사진을 열자마자 확인창이 뜨지 않게.
  const view = (photoId: string | null) => {
    setOpenId(photoId);
    setAsking(false);
  };

  const remove = (photoId: string) => {
    void run(
      "사진을 지우지 못했습니다",
      () => deleteProjectPhoto(projectId, photoId),
      () => {
        toast.success("사진을 지웠습니다");
        view(null);
      }
    );
  };

  if (photos.length === 0) {
    return <EmptyState>아직 올린 사진이 없습니다.</EmptyState>;
  }

  return (
    <>
      <ul className={cn("grid gap-1", gridClassName)} data-testid={testId}>
        {photos.map((photo, index) => (
          <li key={photo.id}>
            <button
              aria-label={`사진 ${index + 1} 크게 보기`}
              className="block aspect-square w-full overflow-hidden rounded-md"
              type="button"
              onClick={() => view(photo.id)}>
              <Thumb
                alt={photo.caption || `현장 사진 ${index + 1}`}
                className="size-full object-cover"
                src={srcOf(projectPhotoPath(projectId, photo.id))}
                withLink={false}
              />
            </button>
          </li>
        ))}
      </ul>

      <Dialog open={current !== null} onOpenChange={(next) => !next && view(null)}>
        <DialogContent className="max-w-3xl">
          <DialogTitle className="text-sm">{current?.caption || "현장 사진"}</DialogTitle>
          <DialogDescription className="text-xs">{current?.meta}</DialogDescription>
          {current && (
            <div className="flex min-h-48 items-center justify-center overflow-hidden rounded-md bg-muted">
              <Thumb
                key={current.id}
                alt={current.caption || "현장 사진"}
                className="max-h-[70vh] min-h-48 w-full object-contain"
                src={srcOf(projectPhotoPath(projectId, current.id))}
              />
            </div>
          )}
          {current?.canDelete && (
            <Button className="self-end" disabled={pending} size="sm" variant="destructive" onClick={() => setAsking(true)}>
              <Trash2 />
              사진 지우기
            </Button>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        destructive
        confirmLabel="지우기"
        description="지운 사진은 되돌릴 수 없습니다."
        open={asking && current !== null}
        testId="photo-delete-confirm"
        title="이 사진을 지울까요?"
        onConfirm={() => current && remove(current.id)}
        onOpenChange={setAsking}
      />
    </>
  );
};

export default PhotoGrid;
